import { useState } from 'react'
import { PageHeader } from '@/app/shell/PageHeader'
import { ADMIN_USERS } from '@/mocks'
import { Card, KLabel } from '@/ui/card'
import { Chip } from '@/ui/chip'
import { cn } from '@/lib/utils'

function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      role="switch"
      aria-checked={on}
      className={cn('relative h-5 w-9 shrink-0 rounded-full transition-colors', on ? 'bg-primary' : 'bg-input')}
    >
      <span className={cn('absolute top-0.5 size-4 rounded-full bg-white transition-all', on ? 'left-4' : 'left-0.5')} />
    </button>
  )
}

const FLAG_DEFS: { key: string; label: string; desc: string }[] = [
  { key: 'roleOverride', label: 'Demo role override', desc: 'Sign in as any persona (non-prod).' },
  { key: 'scadaScenarios', label: 'SCADA presenter scenarios', desc: 'Trigger normal / degrading / incident.' },
  { key: 'aiAutoAnswer', label: 'AI auto-answer', desc: 'Confirming a mapping answers the question.' },
]

export function AdminPage() {
  const [flags, setFlags] = useState<Record<string, boolean>>({ roleOverride: true, scadaScenarios: true, aiAutoAnswer: true })

  return (
    <>
      <PageHeader
        title="Admin"
        subtitle={
          <>
            <span>{ADMIN_USERS.length} users</span>
            <Chip>simulated</Chip>
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
        <div>
          <KLabel className="mb-2">Users &amp; roles</KLabel>
          <Card className="overflow-hidden p-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-faint">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Last active</th>
                </tr>
              </thead>
              <tbody>
                {ADMIN_USERS.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{u.name}</div>
                      <div className="text-[11.5px] text-faint">{u.email}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{u.role}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{u.lastActive}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="mt-3 text-[11.5px] text-faint">
            The legacy Neo4j graph explorer and DynamoDB browser are intentionally removed.
          </p>
        </div>

        <div>
          <KLabel className="mb-2">Feature flags</KLabel>
          <Card className="space-y-4">
            {FLAG_DEFS.map((f) => (
              <div key={f.key} className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[13px] font-medium">{f.label}</div>
                  <div className="text-[11.5px] text-faint">{f.desc}</div>
                </div>
                <Switch on={!!flags[f.key]} onClick={() => setFlags((p) => ({ ...p, [f.key]: !p[f.key] }))} />
              </div>
            ))}
          </Card>
        </div>
      </div>
    </>
  )
}
